/**
 * Mobile device and connection detection utilities
 * Detects mobile devices and slow/metered network connections
 */

/**
 * Checks if the current device is a mobile phone
 * Tests the user agent string against known mobile device patterns
 * iPad is intentionally excluded as iPadOS 13+ reports a desktop user agent
 *
 * @returns {boolean} True if the device is a mobile phone
 */
function _isMobileDevice() {
  if (typeof navigator === 'undefined' || !navigator.userAgent) {
    return false;
  }

  return /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * Checks if the network connection is slow or metered
 * Uses the Network Information API to detect data saver mode
 * and slow effective connection types (slow-2g, 2g, 3g, or slow 4g under 5 Mbps)
 *
 * @returns {boolean} True if the connection is slow or metered
 */
function _isSlowOrMeteredConnection() {
  const connection = navigator?.connection || navigator?.mozConnection || navigator?.webkitConnection;

  if (!connection) {
    return false;
  }

  if (connection.saveData === true) {
    return true;
  }

  if (['slow-2g', '2g', '3g'].includes(connection.effectiveType)) {
    return true;
  }

  if (connection.effectiveType === '4g' && (connection.rtt > 150 || connection.downlink < 5)) {
    return true;
  }

  return false;
}

/**
 * Determines if the user is on a mobile device or slow connection
 * Used to decide whether to serve preview (optimized) files
 * instead of raw source files
 *
 * @returns {boolean} True if mobile device or slow/metered connection
 */
export function isMobileOrSlowConnection() {
  return _isMobileDevice() || _isSlowOrMeteredConnection();
}
